import ReelsFeed from "@/components/ReelsFeed";
import { MembershipFloatingCta } from "@/components/MembershipFloatingCta";
import { useSEO } from "@/hooks/useSEO";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import type { PremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { getReelsPlaybackIdsPreset } from "@/lib/reelsPlaybackPreset";
import { Eye, EyeOff, Link2, Lock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";

const SESSION_FULL = "reels_auth_full";
const SESSION_SHARE = "reels_auth_share";

type AccessMode = "full" | "share";
type ShareKind = "share_link" | "secret";

type AccessOk = {
  mode: AccessMode;
  videoLimit: number | null;
  expiresAt?: string;
  /** Playlist fija del share link (admin eligió videos al crear el link) */
  playbackIds?: string[];
  kind?: ShareKind;
  maxOpens?: number | null;
  useCount?: number;
  remainingOpens?: number | null;
  burned?: boolean;
  customMessage?: string | null;
  /** Token for secret-share engagement beacons */
  engagementToken?: string | null;
};

/**
 * /reels — private vertical feed + shareable reels links.
 * - Password → full catalog
 * - ?t=TOKEN → reels compartidos: N videos fijos, expire, max aberturas
 */
const Reels = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = (searchParams.get("t") || "").trim();

  const [access, setAccess] = useState<AccessOk | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [inviteCfg, setInviteCfg] = useState<PremiumInvitePublicConfig | null>(null);

  useSEO({
    title: "Reels Privados",
    description: "Contenido privado",
    keywords: "privado",
    ogImage: BRAND_OG_IMAGE_URL,
    ogTitle: "Reels Privados",
    ogDescription: "Contenido privado",
    type: "video.other",
    robots: "noindex, nofollow",
  });

  // Restore session / auto-validate token
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (tokenFromUrl) {
          const r = await fetch("/api/reels/access", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: tokenFromUrl }),
          });
          const data = (await r.json()) as {
            ok?: boolean;
            mode?: AccessMode;
            kind?: ShareKind;
            videoLimit?: number;
            expiresAt?: string;
            message?: string;
            playbackIds?: string[];
            maxOpens?: number | null;
            useCount?: number;
            remainingOpens?: number | null;
            burned?: boolean;
            customMessage?: string | null;
          };
          if (!cancelled && r.ok && data.ok && data.mode === "share") {
            const playlist = Array.isArray(data.playbackIds)
              ? data.playbackIds.map(String).filter(Boolean)
              : [];
            const kind = data.kind === "secret" ? "secret" : "share_link";
            const acc: AccessOk = {
              mode: "share",
              kind,
              videoLimit:
                playlist.length ||
                (typeof data.videoLimit === "number" ? data.videoLimit : 3),
              expiresAt: data.expiresAt,
              playbackIds: playlist.length ? playlist : undefined,
              maxOpens: data.maxOpens,
              useCount: data.useCount,
              remainingOpens: data.remainingOpens,
              burned: data.burned,
              customMessage: data.customMessage ?? null,
              engagementToken: kind === "secret" ? tokenFromUrl : null,
            };
            setAccess(acc);
            try {
              sessionStorage.setItem(
                SESSION_SHARE,
                JSON.stringify({ token: tokenFromUrl, ...acc }),
              );
            } catch {
              /* ignore */
            }
            setBootstrapping(false);
            return;
          }
          if (!cancelled && !r.ok) {
            setError(
              data.message ||
                (r.status === 404
                  ? "Este link de reels ya no está disponible"
                  : "Link inválido o expirado"),
            );
          }
        } else {
          try {
            if (sessionStorage.getItem(SESSION_FULL) === "1") {
              if (!cancelled) {
                setAccess({ mode: "full", videoLimit: null });
                setBootstrapping(false);
                return;
              }
            }
            const raw = sessionStorage.getItem(SESSION_SHARE);
            if (raw) {
              const parsed = JSON.parse(raw) as AccessOk & { token?: string };
              if (
                parsed.mode === "share" &&
                parsed.expiresAt &&
                new Date(parsed.expiresAt).getTime() > Date.now()
              ) {
                if (!cancelled) {
                  setAccess({
                    mode: "share",
                    videoLimit: parsed.videoLimit ?? 3,
                    expiresAt: parsed.expiresAt,
                    playbackIds: parsed.playbackIds?.length
                      ? parsed.playbackIds
                      : undefined,
                  });
                  setBootstrapping(false);
                  return;
                }
              }
            }
          } catch {
            /* ignore */
          }
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  useEffect(() => {
    if (!access) return;
    let cancelled = false;
    const run = async () => {
      try {
        const cfg = await fetchPremiumInvitePublicConfig();
        if (!cancelled) setInviteCfg(cfg);
      } catch {
        if (!cancelled) setInviteCfg(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [access]);

  useEffect(() => {
    if (!access) return;
    const ph = (
      window as Window & {
        posthog?: {
          stopSessionRecording?: () => void;
          startSessionRecording?: () => void;
        };
      }
    ).posthog;
    ph?.stopSessionRecording?.();
    return () => {
      ph?.startSessionRecording?.();
    };
  }, [access]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    const originalTouchAction = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
    document.body.style.touchAction = "none";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
      document.body.style.touchAction = originalTouchAction;
      document.documentElement.style.overflow = "";
    };
  }, []);

  const submitPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError("");
      try {
        const r = await fetch("/api/reels/access", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        });
        const data = (await r.json()) as {
          ok?: boolean;
          mode?: AccessMode;
          message?: string;
        };
        if (r.ok && data.ok && data.mode === "full") {
          setAccess({ mode: "full", videoLimit: null });
          try {
            sessionStorage.setItem(SESSION_FULL, "1");
          } catch {
            /* ignore */
          }
          setPassword("");
        } else {
          setError(data.message || "Contraseña incorrecta");
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          setPassword("");
        }
      } catch {
        setError("Error de red");
      } finally {
        setSubmitting(false);
      }
    },
    [password],
  );

  const playbackIdsPreset = useMemo(() => {
    // Share link with explicit playlist → that is the /reels catalog for this token
    if (access?.mode === "share" && access.playbackIds?.length) {
      return access.playbackIds;
    }
    return getReelsPlaybackIdsPreset(inviteCfg);
  }, [access, inviteCfg]);

  const shareLimit =
    access?.mode === "share"
      ? Math.max(
          1,
          access.playbackIds?.length ||
            access.videoLimit ||
            inviteCfg?.reels_teaser_count ||
            3,
        )
      : null;

  if (bootstrapping) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
      </div>
    );
  }

  if (!access) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
          }
          .shake { animation: shake 0.5s ease-in-out; }
        `}</style>

        <div className="w-full max-w-sm px-8">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
              {tokenFromUrl ? (
                <Link2 className="h-8 w-8 text-white/60" />
              ) : (
                <Lock className="h-8 w-8 text-white/60" />
              )}
            </div>
            <h1 className="mb-2 text-xl font-medium text-white">
              {tokenFromUrl ? "Reels no disponibles" : "Contenido Privado"}
            </h1>
            <p className="text-sm text-white/50">
              {tokenFromUrl
                ? error ||
                  "Este link expiró, se usó o ya no está activo. Pedí un link de reels nuevo."
                : "Ingresá la contraseña o usá un link de acceso compartido."}
            </p>
          </div>

          <form onSubmit={(e) => void submitPassword(e)} className="space-y-4">
            <div className={`relative ${isShaking ? "shake" : ""}`}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="Contraseña"
                className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 pr-12 text-white placeholder-white/40 transition-colors focus:border-white/40 focus:bg-white/15 focus:outline-none"
                autoFocus
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white/60"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            {error && (
              <p className="text-center text-sm text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-white py-3 font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-50"
            >
              {submitting ? "…" : "Entrar"}
            </button>
          </form>

          <div className="mt-8 text-center">
            <a
              href="/"
              className="text-sm text-white/40 transition-colors hover:text-white/60"
            >
              ← Volver al inicio
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-dvh max-h-dvh w-full overflow-hidden">
      <ReelsFeed
        playbackIdsPreset={playbackIdsPreset}
        abVariantOverride={
          shareLimit != null ? "three_teaser" : undefined
        }
        teaserReelCount={shareLimit ?? inviteCfg?.reels_teaser_count ?? 3}
        abVariantFallback={
          inviteCfg?.reels_player_variant === "classic"
            ? "classic"
            : "three_teaser"
        }
        freeTelegramUrl={inviteCfg?.free_telegram_url || ""}
        endScreenCtaLabels={{
          headline: inviteCfg?.end_screen_headline || undefined,
          freeTelegram: inviteCfg?.end_screen_free_cta_label || undefined,
          vip: inviteCfg?.end_screen_vip_cta_label || undefined,
        }}
        engagementToken={access.engagementToken || null}
        onVideoPlay={(index, playbackId) => {
          console.debug(`Reel ${index + 1} playing:`, playbackId);
        }}
        onVideoChange={(index, playbackId) => {
          console.debug(`Navigated to reel ${index + 1}:`, playbackId);
        }}
        onReachEnd={() => {
          console.debug("User watched all reels");
        }}
      />
      {access.mode === "full" && <MembershipFloatingCta />}
      {access.mode === "share" && shareLimit != null && (
        <div className="pointer-events-none absolute left-0 right-0 top-0 z-20 flex flex-col items-center gap-1.5 pt-[max(0.5rem,env(safe-area-inset-top))]">
          <span className="rounded-full bg-black/55 px-3 py-1 text-[10px] font-medium tracking-wide text-white/80 ring-1 ring-white/10 backdrop-blur-sm">
            Reels · {shareLimit} video{shareLimit === 1 ? "" : "s"}
            {access.maxOpens != null && access.useCount != null
              ? ` · vista ${access.useCount}/${access.maxOpens}`
              : ""}
            {access.burned ? " · se quema" : ""}
            {access.expiresAt
              ? ` · expira ${new Date(access.expiresAt).toLocaleString()}`
              : ""}
          </span>
          {access.customMessage ? (
            <span className="max-w-md rounded-lg bg-black/50 px-3 py-1.5 text-center text-[11px] text-white/60 ring-1 ring-white/10">
              {access.customMessage}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default Reels;
